/* iter55z-j — EditorSearchDialog: 2-tab search dialog.
 *
 * Tab 1: THIS CHAPTER — client-side find/highlight in the current
 *        chapter body. Reports the # of matches, gives Prev/Next
 *        buttons, and calls `onJumpTo(index)` so the editor can scroll.
 * Tab 2: WHOLE PROJECT — hits the backend `/api/projects/{pid}/search`
 *        endpoint which spans chapters + story bible + characters.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, ArrowUp, ArrowDown, BookOpenText, Sparkles, User, Replace as ReplaceIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function EditorSearchDialog({
  open, onOpenChange, projectId, chapterId, chapterContent, onJumpToChapter,
  onReplaceInChapter,
}) {
  const [tab, setTab] = useState('chapter');
  const [query, setQuery] = useState('');
  // iter55f97 — Find & Replace additions.
  const [replaceQuery, setReplaceQuery] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [chapterHits, setChapterHits] = useState([]);
  const [projectHits, setProjectHits] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  // Autofocus the search box each time the dialog opens so users can
  // start typing immediately after Cmd/Ctrl-clicking the magnifier.
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
    if (!open) {
      setQuery(''); setReplaceQuery('');
      setChapterHits([]); setProjectHits(null); setCursor(0);
    }
  }, [open]);

  // In-chapter find is cheap enough to run on every keystroke.
  useEffect(() => {
    const q = query;
    if (q.length < 1 || !chapterContent) {
      setChapterHits([]);
      setCursor(0);
      return;
    }
    const hay = caseSensitive ? chapterContent : chapterContent.toLowerCase();
    const needle = caseSensitive ? q : q.toLowerCase();
    if (needle.length === 0) { setChapterHits([]); setCursor(0); return; }
    const hits = [];
    let i = 0;
    while (i !== -1 && hits.length < 500) {
      i = hay.indexOf(needle, i);
      if (i === -1) break;
      hits.push(i);
      i += needle.length;
    }
    setChapterHits(hits);
    setCursor(0);
  }, [query, chapterContent, caseSensitive]);

  const runProjectSearch = useCallback(async () => {
    if (!projectId || query.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/search`, {
        params: { q: query.trim() }, withCredentials: true,
      });
      setProjectHits(r.data?.hits || []);
    } catch {
      setProjectHits([]);
    } finally {
      setBusy(false);
    }
  }, [projectId, query]);

  useEffect(() => {
    if (tab !== 'project') return undefined;
    const t = setTimeout(runProjectSearch, 250);
    return () => clearTimeout(t);
  }, [tab, runProjectSearch]);

  const snippet = useMemo(() => {
    const q = query.trim();
    if (!q || !chapterContent || chapterHits.length === 0) return null;
    const idx = Math.min(cursor, chapterHits.length - 1);
    const start = Math.max(0, chapterHits[idx] - 60);
    const end   = Math.min(chapterContent.length, chapterHits[idx] + q.length + 60);
    const before = chapterContent.slice(start, chapterHits[idx]);
    const hit    = chapterContent.slice(chapterHits[idx], chapterHits[idx] + q.length);
    const after  = chapterContent.slice(chapterHits[idx] + q.length, end);
    return { before, hit, after, start, prefix: start > 0, suffix: end < chapterContent.length };
  }, [chapterHits, cursor, chapterContent, query]);

  const step = (delta) => {
    if (chapterHits.length === 0) return;
    setCursor((c) => (c + delta + chapterHits.length) % chapterHits.length);
  };

  /* iter55f97 — Find & Replace helpers. Case-insensitive by default;
   * `caseSensitive` flips both the find and the replace matcher.
   * Both actions early-out cleanly when there's nothing to replace so
   * the button never fires an empty save. */
  const _rebuildAfterReplace = (nextContent) => {
    if (!onReplaceInChapter) {
      toast.error('Replace unavailable in this context.');
      return null;
    }
    return nextContent;
  };

  const _findFirstIndex = (hay, needle) => {
    if (caseSensitive) return hay.indexOf(needle);
    const low = hay.toLowerCase();
    const idx = low.indexOf(needle.toLowerCase());
    return idx;
  };

  const replaceOne = async () => {
    if (!query || replacing) return;
    if (chapterHits.length === 0) { toast.info('Nothing to replace.'); return; }
    const src = chapterContent || '';
    // Replace the current-cursor match; when the actual char slice at
    // that index equals the query (case-sensitively) we can splice
    // directly. Otherwise (case-insensitive find) preserve the
    // original casing region by splice + insert.
    const idx = chapterHits[Math.min(cursor, chapterHits.length - 1)];
    if (idx == null || idx < 0) return;
    const next = src.slice(0, idx) + replaceQuery + src.slice(idx + query.length);
    const rebuilt = _rebuildAfterReplace(next);
    if (rebuilt == null) return;
    setReplacing(true);
    try {
      await onReplaceInChapter(rebuilt);
      toast.success('Replaced 1 match.');
    } catch { toast.error('Could not save replacement.'); }
    finally { setReplacing(false); }
  };

  const replaceAll = async () => {
    if (!query || replacing) return;
    const src = chapterContent || '';
    if (!src) { toast.info('Nothing to replace.'); return; }
    let out;
    let count = 0;
    if (caseSensitive) {
      // Direct split/join to avoid regex escaping.
      const parts = src.split(query);
      count = parts.length - 1;
      out = parts.join(replaceQuery);
    } else {
      // Rebuild using our own case-insensitive indexOf loop so we
      // don't depend on user input being regex-safe.
      const low = src.toLowerCase();
      const nq  = query.toLowerCase();
      let cursor2 = 0;
      const chunks = [];
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const hit = low.indexOf(nq, cursor2);
        if (hit === -1) { chunks.push(src.slice(cursor2)); break; }
        chunks.push(src.slice(cursor2, hit));
        chunks.push(replaceQuery);
        cursor2 = hit + nq.length;
        count += 1;
      }
      out = chunks.join('');
    }
    if (count === 0) { toast.info('No matches to replace.'); return; }
    const rebuilt = _rebuildAfterReplace(out);
    if (rebuilt == null) return;
    setReplacing(true);
    try {
      await onReplaceInChapter(rebuilt);
      toast.success(`Replaced ${count} match${count === 1 ? '' : 'es'}.`);
    } catch { toast.error('Could not save replacements.'); }
    finally { setReplacing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="editor-search-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-4 w-4 text-primary" /> Search
          </DialogTitle>
          <DialogDescription>
            Find text in the current chapter, or across the whole project (chapters, Story Bible, characters).
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find in chapter — type at least 1 character…"
            className="h-9 text-[13px]"
            data-testid="editor-search-input"
          />
          {tab === 'chapter' && chapterHits.length > 0 && (
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <Button size="sm" variant="ghost" className="h-8 px-2"
                       onClick={() => step(-1)} data-testid="editor-search-prev"
                       aria-label="Previous match">
                <ArrowUp className="h-3.5 w-3.5" />
              </Button>
              <span className="font-mono-stat" data-testid="editor-search-counter">
                {cursor + 1} / {chapterHits.length}
              </span>
              <Button size="sm" variant="ghost" className="h-8 px-2"
                       onClick={() => step(1)} data-testid="editor-search-next"
                       aria-label="Next match">
                <ArrowDown className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>

        {/* iter55f97 — Find & Replace row (only in the This-chapter tab) */}
        {tab === 'chapter' && (
          <div className="flex items-center gap-2" data-testid="editor-search-replace-row">
            <Input
              value={replaceQuery}
              onChange={(e) => setReplaceQuery(e.target.value)}
              placeholder="Replace with…"
              className="h-9 text-[13px]"
              data-testid="editor-search-replace-input"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={replaceOne}
              disabled={replacing || chapterHits.length === 0 || !query}
              className="h-9 gap-1"
              data-testid="editor-search-replace-one"
              title="Replace the current match"
            >
              {replacing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ReplaceIcon className="h-3.5 w-3.5" />}
              Replace
            </Button>
            <Button
              size="sm"
              onClick={replaceAll}
              disabled={replacing || chapterHits.length === 0 || !query}
              className="h-9 gap-1"
              data-testid="editor-search-replace-all"
              title="Replace every match in this chapter"
            >
              Replace all
            </Button>
            <label className="flex items-center gap-1 text-[10.5px] text-muted-foreground" title="Case sensitive">
              <input
                type="checkbox"
                checked={caseSensitive}
                onChange={(e) => setCaseSensitive(e.target.checked)}
                data-testid="editor-search-case-sensitive"
              />
              Aa
            </label>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="chapter" data-testid="editor-search-tab-chapter" className="flex-1">
              This chapter{chapterHits.length ? ` (${chapterHits.length})` : ''}
            </TabsTrigger>
            <TabsTrigger value="project" data-testid="editor-search-tab-project" className="flex-1">
              Whole project{Array.isArray(projectHits) ? ` (${projectHits.length})` : ''}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="chapter" className="mt-3">
            {!query.trim() && (
              <p className="text-[12px] text-muted-foreground">
                Start typing to find matches inside this chapter.
              </p>
            )}
            {query.length >= 1 && chapterHits.length === 0 && (
              <p className="text-[12px] text-muted-foreground" data-testid="editor-search-chapter-empty">
                No matches in this chapter.
              </p>
            )}
            {snippet && (
              <div
                data-testid="editor-search-chapter-snippet"
                className="rounded-md border border-panel-border bg-card p-3 font-serif text-[13px] leading-relaxed"
              >
                {snippet.prefix && <span className="text-muted-foreground">…</span>}
                <span>{snippet.before}</span>
                <mark className="rounded-sm bg-primary/25 px-0.5 text-foreground">{snippet.hit}</mark>
                <span>{snippet.after}</span>
                {snippet.suffix && <span className="text-muted-foreground">…</span>}
              </div>
            )}
          </TabsContent>

          <TabsContent value="project" className="mt-3">
            {busy && <p className="text-[12px] text-muted-foreground">Searching…</p>}
            {!busy && Array.isArray(projectHits) && projectHits.length === 0 && (
              <p className="text-[12px] text-muted-foreground" data-testid="editor-search-project-empty">
                No matches across the project.
              </p>
            )}
            {!busy && Array.isArray(projectHits) && projectHits.length > 0 && (
              <ul className="scroll-thin max-h-[45vh] space-y-2 overflow-y-auto pr-1" data-testid="editor-search-project-results">
                {projectHits.map((h, i) => (
                  <li
                    key={i}
                    className="rounded-md border border-panel-border bg-card p-2 hover:bg-secondary/40"
                    data-testid={`editor-search-hit-${h.kind}`}
                  >
                    <button
                      type="button"
                      className="flex w-full items-start gap-2 text-left"
                      onClick={() => {
                        if (h.kind === 'chapter' && onJumpToChapter) {
                          onJumpToChapter(h.chapter_id);
                          onOpenChange(false);
                        }
                      }}
                    >
                      <span className="mt-0.5">
                        {h.kind === 'chapter' && <BookOpenText className="h-3.5 w-3.5 text-primary" />}
                        {h.kind === 'story_bible' && <Sparkles className="h-3.5 w-3.5 text-fuchsia-500" />}
                        {h.kind === 'character' && <User className="h-3.5 w-3.5 text-emerald-600" />}
                      </span>
                      <span className="flex-1">
                        <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                          {h.kind === 'chapter' && `Chapter ${h.number} · ${h.title || 'Untitled'}`}
                          {h.kind === 'story_bible' && `Story Bible · ${h.field}`}
                          {h.kind === 'character' && `Character · ${h.name} · ${h.field}`}
                          {h.count > 1 && ` · ${h.count} matches`}
                        </span>
                        <div className="mt-0.5 text-[12.5px] text-foreground/90">
                          {h.snippet}
                        </div>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
